import { supabase } from './api.js';

// ============================================================
// REP SERVICE — AI Prompt Engineering v2
// Talks to the backend's rep/technique endpoints. Follows the exact
// convention already established in promptService.js (this codebase has
// no shared fetch-with-auth helper — every caller resolves its own
// token from supabase.auth.getSession() and attaches it manually):
// BACKEND_URL from VITE_APP_API_URL, Authorization: Bearer <token> on
// every call.
// ============================================================

const BACKEND_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:3000/api';

const authHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  return {
    'Content-Type':  'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

const handle = async (res) => {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || body.message || 'Request failed. Please try again.');
    // Tags this as "reached the server, got a bad response" so callers can
    // tell it apart from fetch() itself rejecting (server unreachable —
    // see useLessons.js's TypeError check).
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/** All published reps for a lesson, in position order. */
export const getReps = async (lessonId) => {
  const res = await fetch(`${BACKEND_URL}/reps?lessonId=${lessonId}`, { headers: await authHeaders() });
  const { reps } = await handle(res);
  return reps;
};

/** The real, stable-id lookup. */
export const getRep = async (repId) => {
  const res = await fetch(`${BACKEND_URL}/reps/${repId}`, { headers: await authHeaders() });
  const { rep } = await handle(res);
  return rep;
};

/** Fire-and-forget open-event log (health metrics only — see
 * migrations/026_proof_plan.sql) — never blocks or throws into the caller. */
export const logRepOpen = async (repId) => {
  try {
    await fetch(`${BACKEND_URL}/reps/${repId}/open`, { method: 'POST', headers: await authHeaders() });
  } catch {
    // best-effort — a missed log row isn't worth surfacing
  }
};

/**
 * Backward-compatible position-based lookup — resolves "lesson N,
 * position M" to the real rep, the same way old bookmarked
 * /exercise/:exerciseId URLs used to. See backend Rep.findByLessonAndOrder.
 */
export const getRepByPosition = async (lessonId, position) => {
  const res = await fetch(`${BACKEND_URL}/lessons/${lessonId}/reps/${position}`, { headers: await authHeaders() });
  const { rep } = await handle(res);
  return rep;
};

/**
 * The sole scoring entry point for v2 — replaces promptService.runPrompt.
 * Streams newline-delimited JSON stage events (see backend routes/reps.js)
 * rather than awaiting one response: `onEvent` is called once per line as
 * it arrives — `{type:'stage', stage, status, ...}` while the pipeline
 * runs, then exactly one `{type:'result', result}` or `{type:'error', ...}`
 * to close it out. Sandbox rebuild spec 1.2 requires the trace to reflect
 * real backend timing, which a single awaited fetch can't provide.
 */
export const runRep = async ({ repId, submission, onEvent }) => {
  const res = await fetch(`${BACKEND_URL}/reps/${repId}/run`, {
    method:  'POST',
    headers: await authHeaders(),
    body:    JSON.stringify({ submission }),
  });

  // A non-2xx here means the request never reached the streaming handler at
  // all (auth failure, 400 validation) — there's no NDJSON body to read.
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    let message = body.error || body.message || 'Request failed. Please try again.';
    // 429: say how long to wait. Retry-After (seconds) is readable cross-origin
    // only because the backend lists it in CORS exposedHeaders.
    const retryAfter = Number(res.headers.get('Retry-After'));
    if (res.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
      const mins = Math.ceil(retryAfter / 60);
      message += mins > 1 ? ` Try again in about ${mins} minutes.` : ' Try again in about a minute.';
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let streamDone = false;
  while (!streamDone) {
    const { done, value } = await reader.read();
    streamDone = done;
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let newlineIndex;
    while ((newlineIndex = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, newlineIndex);
      buf = buf.slice(newlineIndex + 1);
      if (line.trim()) onEvent(JSON.parse(line));
    }
  }
};

/**
 * Compare reps: runs the learner's two prompts and returns { a, b }, the two outputs, so they
 * can see both BEFORE they choose. Not a run: nothing is scored or saved.
 */
export const previewCompareOutputs = async ({ repId, promptA, promptB }) => {
  const res = await fetch(`${BACKEND_URL}/reps/${repId}/outputs`, {
    method:  'POST',
    headers: await authHeaders(),
    body:    JSON.stringify({ promptA, promptB }),
  });
  const { outputs } = await handle(res);
  return outputs;
};

/**
 * Re-sends the SAVE of a pass that was scored but not stored (the run result
 * had `saved: false`). Not a re-score: the server saves the score it recorded
 * for `executionId`, and nothing about the score comes from here.
 */
export const retrySaveProgress = async ({ repId, executionId }) => {
  const res = await fetch(`${BACKEND_URL}/reps/${repId}/save-progress`, {
    method:  'POST',
    headers: await authHeaders(),
    body:    JSON.stringify({ executionId }),
  });
  return handle(res);
};

/** All techniques for the ai-prompting skill — id, key, name, phase. */
export const getTechniques = async () => {
  const res = await fetch(`${BACKEND_URL}/techniques`, { headers: await authHeaders() });
  const { techniques } = await handle(res);
  return techniques;
};

export const getTechniqueMastery = async () => {
  const res = await fetch(`${BACKEND_URL}/techniques/mastery`, { headers: await authHeaders() });
  const { mastery } = await handle(res);
  return mastery;
};

/** Prompt 4 Part B4: what's due for review right now, each item already
 * resolved to an actual rep (lessonId + position) the learner can open. */
export const getDueReviews = async () => {
  const res = await fetch(`${BACKEND_URL}/techniques/reviews/due`, { headers: await authHeaders() });
  return handle(res); // { reviews, count }
};

/**
 * All published lessons — id, title, phase, etc. Used to replace
 * terminalTokens.js's LESSON_NAMES with the DB-backed title (see that
 * file's own note on the 11/13 name mismatch this closes).
 */
export const getLessons = async () => {
  const res = await fetch(`${BACKEND_URL}/lessons`, { headers: await authHeaders() });
  const { lessons } = await handle(res);
  return lessons;
};

export default { getReps, getRep, getRepByPosition, logRepOpen, runRep, getTechniques, getTechniqueMastery, getDueReviews, getLessons };
